// Story 1.19 — TanStack mutation for DELETE /api/v1/account/delete-request.
//
// On success (204): invalidates account-deletion-status so the page
// re-renders without the PendingDeletionPanel.
// On 409 ACCOUNT_DELETION_TOO_LATE: caller shows a toast with the
// support-email contact (the worker has already started
// anonymization; the cancellation can no longer be honored from this
// surface).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type CancelDeletionError = {
  code: string;
  message: string;
};

async function deleteDeletionRequest(): Promise<void> {
  const response = await fetch("/api/v1/account/delete-request", {
    method: "DELETE",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CancelDeletionError = {
      code: "DELETION_CANCEL_FAILED",
      message: `Cancellation failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
}

export function useCancelDeletion() {
  const queryClient = useQueryClient();
  return useMutation<void, CancelDeletionError, void>({
    mutationFn: () => deleteDeletionRequest(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-deletion-status"] });
    },
  });
}
